import { Injectable } from '@angular/core';
import {
  BirdsViewService,
  WS_ENDPOINT,
  BIRD_EYE_WS_UI_ERRORS_MAP,
  BIRD_EYE_BUFFER_ON,
  BIRD_EYE_SOCKET_DELAY,
  BIRD_EYE_BUFFER_MIN_SIZE,
  BirdsEyeLocation,
  BIRD_EYE_BUFFER_MAX_SIZE,
  BIRD_EYE_DEBUG_ON,
} from './birds-view.service';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { calculateNextPoint } from './mock-route';
import { getBearingFromCompass } from '../helpers/bearings-convertor';

const BIRD_EYE_MOCK_ON = false;
const MOCK_BIRD_EYE_RANDOM_SPEED = false;
const MOCK_BIRD_EYE_RANDOM_BEARING = false;
const MOCK_BIRD_EYE_RANDOM_SOCKET_DELAY = false;
const MOCK_BIRD_EYE_WS_ERROR_IDX = undefined; // undefined, 0, 1, 2, 3, 4, 5, 6
const MOCK_BIRD_EYE_WS_WARNING_IDX = undefined; // undefined, 7

@Injectable({
  providedIn: 'root',
})
export class BirdsViewRxjsService extends BirdsViewService {
  private readonly wsConfig: WebSocketSubjectConfig<any>;
  private mockSocketInterval = null;

  protected socket: WebSocketSubject<any>;

  constructor() {
    super();

    this.wsConfig = {
      url: WS_ENDPOINT,
      openObserver: {
        next: () => {
          if (BIRD_EYE_DEBUG_ON) {
            console.debug('[BV Connected]');
          }
          this.isConnected = true;

          // Ping server every 1 second
          this.pingInterval = setInterval(() => {
            this.pingOkStatus('k');
          }, 2000);
        },
      },
      closeObserver: {
        next: () => {
          this.isConnected = false;
          if (BIRD_EYE_DEBUG_ON) {
            console.debug('[BV Disconnected]');
          }
          if (BIRD_EYE_BUFFER_ON) {
            this.stopBufferInterval(true);
          }
          clearInterval(this.pingInterval);
        },
      },
      deserializer: (e) => e,
      serializer: (e) => e,
    };
  }

  public connectToSocket(driverId: string): void {
    if (!this.isConnected) {
      if (this.socket) {
        this.socket.unsubscribe();
      }

      this.wsConfig.url = WS_ENDPOINT + this.buildQueryParams(driverId);
      this.socket = webSocket(this.wsConfig);

      if (BIRD_EYE_MOCK_ON) {
        //TODO: Remove after development
        this.isConnected = true;
        this.bufferReady = false;
        if (BIRD_EYE_DEBUG_ON) {
          console.debug('[BV MOCK Connected]');
        }
      }
    }
  }

  public disconnectFromSocket(): void {
    if (this.isConnected) {
      if (this.socket) {
        this.socket.unsubscribe();
      }
      if (BIRD_EYE_MOCK_ON) {
        //TODO: Remove after development
        this.isConnected = false;
        if (BIRD_EYE_DEBUG_ON) {
          console.debug('[BV MOCK Disconnected]');
        }
        if (BIRD_EYE_BUFFER_ON && this.bufferReady) {
          clearInterval(this.bufferInterval);
          this.bufferReady = false;
          this.bufferedLocations = [];
        }
      }
    }
  }

  protected pingOkStatus(message: string): void {
    if (this.socket) {
      this.socket.next(message);
    }
  }

  public toggleVehicleBirdsView(driverId: string, vehicle?: any): void {
    if (!this.isConnected) {
      this.connectToSocket(driverId);
      this.firstMove = true;
      if (BIRD_EYE_MOCK_ON) {
        //TODO: Remove after development
        this.initMessageMockerCalcNext(vehicle);
      } else {
        this.initMessageListener();
      }
    } else {
      this.disconnectFromSocket();
    }
  }

  private initMessageListener(): void {
    // Subcribe to location messages
    this.nextLocation = null;
    this.socket.subscribe({
      next: (msg: MessageEvent) => {
        if (msg.data.startsWith('ERROR')) {
          if (BIRD_EYE_DEBUG_ON) {
            console.debug('[BV Error] ', msg.data);
          }
          this.locationSubjectEmitError(msg.data);
          this.disconnectFromSocket();
        }

        if (msg.data.startsWith('WARNING')) {
          if (BIRD_EYE_DEBUG_ON) {
            console.debug('[BV Warning] ', msg.data);
          }
          this.locationSubjectEmit(this.parseSocketMessage(msg.data));
        } else {
          /**
           * Location Msg format
           * 'XX.xx,YY.yy,BEARING';
           * */
          if (BIRD_EYE_DEBUG_ON) {
            console.debug('[BV Location] ', msg.data);
          }
          this.nextLocation = this.parseSocketMessage(msg.data);
          this.storeTimestamp();

          if (BIRD_EYE_BUFFER_ON) {
            this.bufferLocation(this.nextLocation);
            if (!this.bufferInterval && this.bufferedLocations.length >= BIRD_EYE_BUFFER_MIN_SIZE) {
              // BUFFER REDADY FOR PERIODICAL EMIT
              this.sendFromBuffer();
              this.startBufferInterval(this.currentBufferIntevalTime);
            }

            if (this.firstMove) {
              // SEND FIRST JUMP
              this.sendFromBuffer();
            }
          } else {
            this.locationSubjectEmit(this.nextLocation);
          }
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  protected override bufferLocation(location: BirdsEyeLocation): void {
    if (this.bufferedLocations.length < BIRD_EYE_BUFFER_MAX_SIZE) {
      this.bufferedLocations.push(location);
      if (BIRD_EYE_DEBUG_ON) {
        console.debug('[BV BUFFER] ++: ', this.bufferedLocations.length);
      }
    }
  }

  protected override sendFromBuffer(): void {
    const sendLocation = () => {
      const nextLocation = this.bufferedLocations.shift();
      this.locationSubjectEmit(nextLocation);
      if (BIRD_EYE_DEBUG_ON) {
        console.debug('[BV BUFFER] --: ', this.bufferedLocations.length);
      }
    };

    if (!this.bufferReady) {
      if (this.firstMove && this.bufferedLocations.length > 0) {
        // SEND FIRST JUMP
        this.firstMove = false;
        sendLocation();
      }
      if (this.bufferedLocations.length >= BIRD_EYE_BUFFER_MIN_SIZE) {
        // BUFFER REDADY FOR PERIODICAL EMIT
        this.bufferReady = true;
        sendLocation();
      }
    } else {
      // INTERVAL ON
      if (this.bufferedLocations.length == 0) {
        // BUFFER EMPTY
        this.bufferReady = false;
        const warn = BIRD_EYE_WS_UI_ERRORS_MAP.get('WARNING: empty buffer');
        this.locationSubjectEmit({
          latitude: 0,
          longitude: 0,
          bearing: 0,
          message: warn,
        });
        console.error('[BV BUFFER] EMPTY: ', this.bufferedLocations.length);
      } else {
        // BUFFER NOT EMPTY
        sendLocation();
      }
    }
  }

  private parseSocketMessage(locationMessage: string): BirdsEyeLocation {
    if (locationMessage.includes('WARNING')) {
      return {
        latitude: 0,
        longitude: 0,
        bearing: 0,
        message: BIRD_EYE_WS_UI_ERRORS_MAP.get(locationMessage),
      };
    }
    const locatioMessageSplitted = locationMessage.split(',');
    return {
      latitude: parseFloat(locatioMessageSplitted[0]),
      longitude: parseFloat(locatioMessageSplitted[1]),
      bearing: parseFloat(locatioMessageSplitted[2]),
    };
  }

  private buildQueryParams(driverId: string): string {
    const token = `${this.tokenService.getUser().token}`;
    return `?token=${token}&driverId=${driverId}`;
  }

  //#region ======================== MOCKS ========================

  private initMessageMockerCalcNext(vehicle: any): void {
    if (!vehicle) {
      return;
    }

    const time = BIRD_EYE_SOCKET_DELAY / 1000; // Time in seconds
    const bearing = typeof vehicle.bearing === 'string' ? getBearingFromCompass(vehicle.bearing) : vehicle.bearing;
    const speed = (vehicle.speed * 1609.34) / 3600;
    this.nextLocation = { latitude: vehicle.lat, longitude: vehicle.lng, bearing: bearing };

    const socketCallback = () => {
      if (!this.isConnected) {
        clearTimeout(this.mockSocketInterval);
        return;
      }
      this.currentLocation = this.nextLocation;
      this.nextLocation = calculateNextPoint(
        this.currentLocation.latitude,
        this.currentLocation.longitude,
        this.currentLocation.bearing,
        speed,
        time,
        MOCK_BIRD_EYE_RANDOM_SPEED,
      );

      if (BIRD_EYE_BUFFER_ON) {
        this.bufferLocation(this.nextLocation);
      } else {
        this.locationSubjectEmit(this.nextLocation);
      }
      this.mockSocketInterval = setTimeout(socketCallback, this.getMockSocketDelay());
    };

    if (MOCK_BIRD_EYE_WS_ERROR_IDX != undefined) {
      const err = Array.from(BIRD_EYE_WS_UI_ERRORS_MAP.keys())[MOCK_BIRD_EYE_WS_ERROR_IDX];
      setTimeout(() => {
        this.locationSubjectEmitError(err);
        this.disconnectFromSocket();
      }, 5000);
    }

    if (MOCK_BIRD_EYE_WS_WARNING_IDX != undefined) {
      const warn = BIRD_EYE_WS_UI_ERRORS_MAP.get(
        Array.from(BIRD_EYE_WS_UI_ERRORS_MAP.keys())[MOCK_BIRD_EYE_WS_WARNING_IDX],
      );
      setTimeout(() => {
        this.locationSubjectEmit({
          latitude: 0,
          longitude: 0,
          bearing: 0,
          message: warn,
        });
      }, 20_000);
    }

    this.mockSocketInterval = setTimeout(socketCallback, this.getMockSocketDelay());

    if (BIRD_EYE_BUFFER_ON) {
      this.bufferReady = false;
      this.bufferedLocations = [];
      this.bufferInterval = setInterval(() => {
        this.sendFromBuffer();
      }, BIRD_EYE_SOCKET_DELAY);
    }
  }

  private getMockSocketDelay(): number {
    // const randPart = Math.random() * BIRDS_EYE_SOCKET_DELAY;
    // const randDelay = BIRDS_EYE_SOCKET_DELAY/2 + randPart;

    if (MOCK_BIRD_EYE_RANDOM_SOCKET_DELAY) {
      const randPart = Math.random() * 4000;
      const randDelay = 4000 + randPart;
      if (BIRD_EYE_DEBUG_ON) {
        console.debug(`[BV MOCK SOCKET NEXT TIMEOUT]: ${randDelay.toFixed(0)} ms`);
      }
      return randDelay;
    } else {
      if (BIRD_EYE_DEBUG_ON) {
        console.debug(`[BV MOCK SOCKET NEXT TIMEOUT]: ${BIRD_EYE_SOCKET_DELAY.toFixed(0)} ms`);
      }
      return BIRD_EYE_SOCKET_DELAY;
    }
  }

  //#endregion ======================== MOCKS ========================
}
